import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    IconButton,
    Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    SttButton,
    SttButtonAccept,
    SttButtonClose,
    SttButtonSave,
    SttButtonCancel,
    SttMenu,
    SttButtonUnsubscribe,
    SttButtonDelete,
    SttButtonRecover,
    SttButtonDownload,
} from '../all';
import { usePermission } from '../../lib/usePermission';
import { PermissionRequirement } from '../../lib/permissions';

type ButtonType =
    | 'accept'
    | 'cancel'
    | 'close'
    | 'delete'
    | 'recover'
    | 'save'
    | 'unsubscribe'
    | 'download'
    | 'component';

export type DialogButton = {
    type?: ButtonType;
    caption?: string;
    icon?: React.ReactNode;
    show?: boolean;
    onClick?: () => void;
    component?: React.ReactNode;
    loading?: boolean;
    disabled?: boolean;
    confirmation?: boolean;
    color?: string;
    /** Permiso v8 que hace falta para ver el botón (p. ej. `<módulo>.modify`). */
    permission?: PermissionRequirement;
};

type MenuItem = {
    caption?: string;
    onClick?: () => void;
    [key: string]: unknown;
};

// Allow falsy values (false, 0, null, undefined) in button arrays for conditional rendering
type DialogButtonOrFalsy = DialogButton | false | 0 | null | undefined;

type Props = {
    title?: string;
    subtitle?: string;
    open?: boolean;
    content?: React.ReactNode;
    onClose?: () => void;
    buttons?: DialogButtonOrFalsy[];
    menu?: MenuItem[];
    minHeight?: number | string;
    maxHeight?: number | string;
    onPressSubtitle?: () => void;
    [key: string]: unknown;
};

/**
 *    Dialog
 *    ======
 *
 *    Props
 *
 *     * string title
 *     * string subtitle
 *     * boolean open
 *    * function onClose
 *    * Component content
 *    * array buttons: botones con los componentes:
 *
 *         - string caption
 *         - Icon icon
 *         - boolean show
 *        - string type: accept, close, save
 *        - function onClick
 *        - Componente component: si se quiere añadir un componente cualquiera
 *
 *     * array menu: arriba a la izquierda saldrían tres puntos con un menú. Más info <SttMenu />
 */
export function SttDialog({
    title,
    subtitle = undefined,
    open = false,
    content,
    onClose,
    buttons,
    menu = undefined,
    minHeight = undefined,
    maxHeight = undefined,
    onPressSubtitle = undefined,
    ...rest
}: Props): React.ReactElement {
    const { can } = usePermission();

    /**
     * Render button
     */
    const renderButton = (
        button: DialogButtonOrFalsy,
        idx: number,
    ): React.ReactElement | null => {
        if (button && (!button.permission || can(button.permission))) {
            let buttonContent: React.ReactNode;

            if (button.show !== false) {
                if (button.component) {
                    buttonContent = button.component;
                } else {
                    switch (button.type) {
                        case 'accept':
                            buttonContent = (
                                <SttButtonAccept
                                    loading={button.loading}
                                    disabled={button.disabled}
                                    caption={button.caption}
                                    onClick={button.onClick ?? (() => {})}
                                />
                            );
                            break;
                        case 'cancel':
                            buttonContent = (
                                <SttButtonCancel
                                    onClick={button.onClick ?? (() => {})}
                                />
                            );
                            break;
                        case 'close':
                            buttonContent = (
                                <SttButtonClose
                                    onClick={button.onClick ?? (() => {})}
                                />
                            );
                            break;
                        case 'delete':
                            buttonContent = (
                                <SttButtonDelete
                                    caption={button.caption}
                                    onClick={button.onClick}
                                    confirmation={!!button.confirmation}
                                />
                            );
                            break;
                        case 'recover':
                            buttonContent = (
                                <SttButtonRecover onClick={button.onClick} />
                            );
                            break;
                        case 'save':
                            buttonContent = (
                                <SttButtonSave
                                    onClick={button.onClick}
                                    loading={button.loading}
                                    disabled={button.disabled}
                                />
                            );
                            break;
                        case 'unsubscribe':
                            buttonContent = (
                                <SttButtonUnsubscribe
                                    onClick={button.onClick ?? (() => {})}
                                />
                            );
                            break;
                        case 'download':
                            buttonContent = (
                                <SttButtonDownload
                                    onClick={button.onClick ?? (() => {})}
                                />
                            );
                            break;
                        case 'component':
                            buttonContent = button.component;
                            break;
                        default:
                            buttonContent = (
                                <SttButton
                                    fullWidth
                                    startIcon={button.icon}
                                    caption={button.caption}
                                    onClick={button.onClick}
                                />
                            );
                            break;
                    }
                }

                return (
                    <Grid key={idx} size="auto">
                        {buttonContent}
                    </Grid>
                );
            }
        }
        return null;
    };

    return (
        <Dialog
            fullWidth
            onClose={onClose}
            open={open}
            PaperProps={{
                style: {
                    minHeight,
                    maxHeight,
                },
            }}
            {...rest}
        >
            {title && (
                <DialogTitle style={{ position: 'relative' }}>
                    <div>{title}</div>

                    {subtitle && (
                        <Box fontSize={15} style={{ color: '#999' }}>
                            {onPressSubtitle ? (
                                <Link
                                    onClick={onPressSubtitle}
                                    underline="hover"
                                    color="primary"
                                    className="cursor-pointer"
                                >
                                    {subtitle}
                                </Link>
                            ) : (
                                subtitle
                            )}
                        </Box>
                    )}

                    {/* TODO: mejorar esta implementación para que sea flexible */}
                    {menu && (
                        <SttMenu
                            {...({ items: menu } as any)}
                            style={{
                                position: 'absolute',
                                top: 10,
                                right: 50,
                            }}
                        />
                    )}

                    {onClose && (
                        <IconButton
                            style={{
                                position: 'absolute',
                                right: 10,
                                top: 10,
                            }}
                            onClick={onClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </DialogTitle>
            )}

            <DialogContent dividers>
                <Box p={1}>{content}</Box>
            </DialogContent>

            {buttons && (
                <DialogActions>
                    <Box p={1} width="100%">
                        <Grid container spacing={2} justifyContent="flex-end">
                            {(buttons as any).map((button: any, idx: any) =>
                                renderButton(button, idx),
                            )}
                        </Grid>
                    </Box>
                </DialogActions>
            )}
        </Dialog>
    );
}
